import jwt_decode from "jwt-decode";
import PropTypes from "prop-types";
import { createContext, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { axiosApiInstance } from "src/api/axios";
import { axiosAuthInstance, setRoleSwitchInProgress } from "src/api/axiosAuth";
import { queryClient } from "src/queryClient";
import {
  initialize as initializeAuth,
  login as loginAuth,
  logout as logoutAuth,
  setRole as setRoleAuth,
  setAvailableRoles,
} from "src/redux/authSlice";
import { store } from "src/redux/store";
import localStorageAvailable from "src/utils/localStorageAvailable";
import logger from "src/utils/logger";
import tokenStorage from "./tokenStorage";
import { isValidToken, setSession } from "./utils";

const BASE_API = import.meta.env.VITE_API_ENDPOINT;

export const AuthContext = createContext(null);

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const state = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  // Ref to prevent double initialization (guards against race conditions)
  const isInitializing = useRef(false);

  const storageAvailable = localStorageAvailable();

  /**
   * Initialize user from persisted Redux state or JWT token
   * Priority: Redux persisted state > JWT token decode
   * This ensures Redux is the single source of truth after initial hydration
   *
   * IMPORTANT: Uses store.getState() to avoid stale closure issues with useCallback
   */
  const initializeUser = useCallback(async () => {
    // Prevent concurrent initialization
    if (isInitializing.current) {
      return;
    }
    isInitializing.current = true;

    try {
      const accessToken = storageAvailable ? tokenStorage.getAccessToken() : "";
      // Read current state directly from store to avoid stale closure
      const currentState = store.getState().auth;

      // If already authenticated via redux-persist, verify token still exists and is valid
      // This prevents redirect loops when token is deleted but redux-persist has stale state
      if (currentState.isAuthenticated && currentState.user && currentState.selectedRole) {
        if (accessToken && isValidToken(accessToken)) {
          if (!currentState.isInitialized) {
            dispatch(initializeAuth({ isAuthenticated: true, user: currentState.user }));
          }
          isInitializing.current = false;
          return;
        }
        // Token missing or invalid - clear stale persisted state
        logger.info("Token missing or invalid, clearing persisted auth state");
      }

      if (accessToken && isValidToken(accessToken)) {
        const decodedToken = jwt_decode(accessToken);

        if (Date.now() >= decodedToken.exp * 1000) {
          logger.info("Token has expired");
          tokenStorage.clearTokens();
          dispatch(initializeAuth({ isAuthenticated: false, user: null }));
          isInitializing.current = false;
          return;
        }

        // Build user object from JWT token (handles both old and new structures)
        const activeRole = decodedToken.active_role || (decodedToken.roles && decodedToken.roles[0]);
        const roles = decodedToken.roles || (activeRole ? [activeRole] : []);

        if (!activeRole) {
          logger.warn("No active role found in token");
          dispatch(initializeAuth({ isAuthenticated: false, user: null }));
          isInitializing.current = false;
          return;
        }

        const user = {
          username: decodedToken.username || activeRole.username,
          external_id: decodedToken.external_id || activeRole.external_id,
          type: activeRole.role,
          roles,
          activeRole,
          selectedRole: activeRole.role,
          customer_name: activeRole.customer_name,
          msisdn: decodedToken.msisdn,
          // Preserve terms dates from localStorage (they come from login response, not JWT)
          terms_and_conditions_date: storageAvailable ? localStorage.getItem("terms_and_conditions_date") : null,
          spar_terms_and_conditions_date: storageAvailable ? localStorage.getItem("spar_terms_and_conditions_date") : null,
          spar_rica_training_video_date: storageAvailable ? localStorage.getItem("spar_rica_training_video_date") : null,
        };

        dispatch(initializeAuth({ isAuthenticated: true, user }));
      } else {
        logger.info("accessToken is not valid or missing");
        dispatch(initializeAuth({ isAuthenticated: false, user: null }));
      }
    } catch (error) {
      logger.error("Auth initialization failed", error);
      dispatch(initializeAuth({ isAuthenticated: false, user: null }));
    } finally {
      isInitializing.current = false;
    }
  }, [storageAvailable, dispatch]); // Removed state dependencies - using store.getState() instead

  useEffect(() => {
    initializeUser();
  }, [initializeUser]);

  const singleSignOn = useCallback(
    async (encrypted_value, iv, partner) => {
      // Decrypt FIRST — only clear session after successful decrypt to avoid
      // stranding the user in a logged-out state on network/server errors
      const { data } = await axiosApiInstance.post(`${BASE_API}/app/sso/decrypt`, {
        encrypted_value,
        iv,
        partner,
      });

      if (data?.status_code === 1001) {
        // Clear previous session only after successful decrypt
        tokenStorage.clearTokens();
        if (storageAvailable) {
          localStorage.removeItem("terms_and_conditions_date");
          localStorage.removeItem("spar_terms_and_conditions_date");
          localStorage.removeItem("spar_rica_training_video_date");
        }
        setSession(null);
        dispatch(logoutAuth());

        const { external_id, msisdn } = data?.data;

        // Handle new active_role structure or fallback to roles array
        const activeRole = data.data.active_role || (data.data.roles && data.data.roles[0]);
        const roles = data.data.roles || (activeRole ? [activeRole] : []);

        // Build complete user object - Redux is the single source of truth
        const user = {
          username: external_id,
          external_id,
          type: "honey",
          roles,
          activeRole,
          msisdn,
          selectedRole: activeRole ? activeRole.role : "honey",
          customer_name: external_id,
        };

        // Set token in localStorage (needed by axios interceptor before Redux hydrates)
        setSession(data.data.token);
        // Redux dispatch - redux-persist handles persistence automatically
        dispatch(loginAuth({ user }));
      } else {
        throw new Error(data?.message || "SSO authentication failed");
      }
    },
    [dispatch, storageAvailable]
  );

  const login = useCallback(
    async (username, password) => {
      const { data } = await axiosApiInstance.post(`${BASE_API}/app/login/password`, {
        username,
        password,
      });

      if (data.status_code === 1001 && data.data) {
        // Handle new token structure with active_role (NEW) or fallback to roles array (OLD)
        const activeRole = data.data.active_role || (data.data.roles && data.data.roles[0]);
        const roles = data.data.roles || (activeRole ? [activeRole] : []);

        if (!activeRole) {
          throw new Error("No active role found");
        }

        // Build complete user object - Redux is the single source of truth
        const user = {
          username,
          type: activeRole.role,
          roles,
          activeRole,
          msisdn: data.data.msisdn,
          selectedRole: activeRole.role,
          customer_name: activeRole.customer_name,
          external_id: data.data.external_id,
          terms_and_conditions_date: data.data.terms_and_conditions_date,
          spar_terms_and_conditions_date: data.data.spar_terms_and_conditions_date,
          spar_rica_training_video_date: data.data.spar_rica_training_video_date,
        };

        // Store terms dates in localStorage (needed for SPAR modal checks before Redux hydrates)
        // These are special cases that need to survive page reload independently
        if (storageAvailable) {
          localStorage.setItem("terms_and_conditions_date", user.terms_and_conditions_date || "");
          localStorage.setItem("spar_terms_and_conditions_date", user.spar_terms_and_conditions_date || "");
          localStorage.setItem("spar_rica_training_video_date", user.spar_rica_training_video_date || "");
        }

        // Set token in localStorage (needed by axios interceptor before Redux hydrates)
        setSession(data.data.tokens.token);
        // Redux dispatch - redux-persist handles persistence of user/role state
        dispatch(loginAuth({ user }));
      } else {
        throw new Error("Invalid username or password");
      }
    },
    [dispatch, storageAvailable]
  );

  const logout = useCallback(() => {
    // Clear React Query cache to prevent data leakage between users
    queryClient.clear();

    // Clear app-specific sessionStorage keys only (not third-party data like Sentry, analytics)
    // These are filter/pagination states that may contain user-specific data
    const sessionStorageKeys = [
      // Pagination states
      "ricaCurrentPage",
      "ricaCurrentRowsPerPage",
      "portingCurrentPage",
      "portingCurrentRowsPerPage",
      "distributorCurrentPage",
      "distributorCurrentRowsPerPage",
      "selfRicaCurrentPage",
      "selfRicaCurrentRowsPerPage",
      "noticeCurrentPage",
      "noticeCurrentRowsPerPage",
      "iotCurrentPage",
      "iotCurrentRowsPerPage",
      "resellerCurrentPage",
      "resellerCurrentRowsPerPage",
      "simCardCurrentPage",
      "simCardCurrentRowsPerPage",
      "messagesCurrentPage",
      "messagesCurrentRowsPerPage",
      // Filter states
      "search",
      "filterStatus",
      "filterService",
      "subDistributorFilterService",
      "filterEndDate",
      "filterStartDate",
      "filterVoiceXdr",
      "filterDataXdr",
      "filterSmsXdr",
      "filterSimType",
      "filterDMA",
      "searchType",
      // Other app state
      "ruleTabValue",
      "email-recovery",
    ];
    sessionStorageKeys.forEach((key) => sessionStorage.removeItem(key));

    // Clear tokens and terms dates from localStorage
    // NOTE: Redux auth state is cleared by logoutAuth() and redux-persist removes persisted data
    tokenStorage.clearTokens();
    if (storageAvailable) {
      localStorage.removeItem("terms_and_conditions_date");
      localStorage.removeItem("spar_terms_and_conditions_date");
      localStorage.removeItem("spar_rica_training_video_date");
      localStorage.removeItem("distributorType");
      // Clean up legacy keys that may still exist
      localStorage.removeItem("username");
      localStorage.removeItem("role");
      localStorage.removeItem("selectedRole");
      localStorage.removeItem("customer_name");
      localStorage.removeItem("external_id");
      localStorage.removeItem("auth"); // Legacy manual auth storage
    }
    setSession(null);
    // Redux dispatch clears auth state; redux-persist removes persisted data
    dispatch(logoutAuth());

    // Reset initialization ref to allow re-initialization
    isInitializing.current = false;

    // Set isInitialized: true with isAuthenticated: false to prevent stuck loading screen
    // Using direct dispatch instead of initializeUser() to avoid circular dependency
    dispatch(initializeAuth({ isAuthenticated: false, user: null }));
  }, [dispatch, storageAvailable]);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      // Cache-bust to prevent browser HTTP caching of stale role data
      const { data } = await axiosAuthInstance.get(`${BASE_API}/app/roles?_t=${Date.now()}`);

      if (data.status_code === 1001 && data.data && data.data.roles) {
        // Update Redux with fetched roles (single source of truth)
        dispatch(setAvailableRoles(data.data.roles));
        logger.debug("Fetched available roles", { count: data.data.roles.length });
        return data.data.roles;
      }
      // Return null for any non-success response - callers can distinguish
      // API failure (null) from "user legitimately has zero roles" (empty array from success path)
      logger.warn("Unexpected API response for roles", { statusCode: data.status_code });
      return null;
    } catch (error) {
      logger.error("Error fetching roles", error);
      return null;
    }
  }, [dispatch]);

  const switchRole = useCallback(
    async (roleId) => {
      try {
        // Set flag to suppress 401 logout for stale requests from old page
        setRoleSwitchInProgress(true);

        const { data } = await axiosAuthInstance.post(`${BASE_API}/app/switch-role`, {
          role_id: roleId,
        });

        if (data.status_code === 1001 && data.data && data.data.active_role) {
          const activeRole = data.data.active_role;
          // Only use roles from API if provided; otherwise preserve existing Redux roles
          const roles = data.data.roles || null;

          // Validate token before setting
          if (!data.data.token || !isValidToken(data.data.token)) {
            logger.error("Invalid token received from switch-role", null, {
              roleId,
              hasToken: !!data.data.token,
            });
            setRoleSwitchInProgress(false);
            return false;
          }

          // Clear React Query cache to prevent stale data
          queryClient.clear();

          // Update token
          setSession(data.data.token);
          if (data.data.refresh_token) {
            tokenStorage.setRefreshToken(data.data.refresh_token);
          }

          // Update Redux with active role and user info
          if (roles) {
            logger.debug("Role switch: updating availableRoles from API", { count: roles.length, roleId });
            dispatch(setRoleAuth({
              activeRole: activeRole,
              roles: roles,
              user: {
                type: activeRole.role,
                selectedRole: activeRole.role,
                customer_name: activeRole.customer_name,
              },
            }));
          } else {
            // API did not return roles - update active role first, then fetch fresh roles
            // This prevents stale role data from being displayed if admin revoked a role
            logger.info("Role switch: API did not return roles, fetching fresh list", { roleId });
            dispatch(setRoleAuth({
              activeRole: activeRole,
              user: {
                type: activeRole.role,
                selectedRole: activeRole.role,
                customer_name: activeRole.customer_name,
              },
            }));
            // Fetch fresh roles asynchronously - don't block the switch
            fetchAvailableRoles().catch((err) => {
              logger.error("Failed to fetch roles after switch", err);
            });
          }

          // Flag is cleared by caller after navigation
          return true;
        }
        setRoleSwitchInProgress(false);
        return false;
      } catch (error) {
        logger.error("Error switching role", error, { roleId });
        setRoleSwitchInProgress(false);
        return false;
      }
    },
    [dispatch, storageAvailable, fetchAvailableRoles]
  );

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "jwt",
        login,
        logout,
        singleSignOn,
        fetchAvailableRoles,
        switchRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
